'use client';

import { Box, Alert, Stack, Divider, Skeleton, Typography, CircularProgress } from '@mui/material';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { Tag } from './market-rankings-badges';
import TagBlock from './market-rankings-tag-block';
import SpecBlock from './market-rankings-spec-block';
import ReviewBlock from './market-rankings-review-block';
import { RankPriceChart } from './market-rankings-charts';
import SectionHeading from './market-rankings-section-heading';
import { buildSignals } from '../utils/market-rankings-signals';
import { countRankedPoints } from '../utils/market-rankings-rank';
import { SOURCE_LABEL } from '../constants/market-rankings-labels';
import DetailCollectBar from './market-rankings-detail-collect-bar';
import DetailMediaBlock from './market-rankings-detail-media-block';
import UnrankedProductPanel from './market-rankings-unranked-panel';
import CrossChannelCard from './market-rankings-cross-channel-card';
import { MetricTile, SignalBadges } from './market-rankings-metrics';
import { ymdToShort, formatPrice } from '../utils/market-rankings-format';
import { foundDetail, hasCollectedDetail } from '../utils/market-rankings-product';

import type { TagSelectHandler } from './market-rankings-tag-block';
import type { MarketProduct } from '../utils/market-rankings-product';
import type { DetailExpandSignal } from './market-rankings-detail-media-block';
import type {

  ProductCrossState,
  ProductDetailState,
  ProductHistoryState,
} from '../hooks/load/fetch/use-product-detail';

/** 추이 그래프를 그리려면 순위가 있는 점이 최소 둘은 있어야 한다 */
const MIN_CHART_POINTS = 2;

const UNCOLLECTED_NOTE =
  '지금 상세페이지를 불러오면 상세 이미지·영상과 AI 태그를 볼 수 있어요.';

/**
 * 다이얼로그와 비교 카드가 **공유하는** 상세 본문.
 *
 * `sections`로 그릴 블록을 고른다(없으면 전부) — 비교 카드는 툴바에서 켜고 끈다.
 *
 * ⚠️ 랭킹에 없는 상품은 통째로 다른 화면이다(`UnrankedProductPanel`). 순위·추이·타채널이
 * 아예 없어서 같은 뼈대로 그리면 빈 블록만 늘어선다.
 */
export default function ProductDetailBody({
  product,
  history,
  cross,
  detail,
  dense,
  sections,
  onSelectTag,
  activeTags,
  expandSignal,
  onCollected,
}: {
  product: MarketProduct;
  history: ProductHistoryState;
  cross: ProductCrossState;
  detail: ProductDetailState;
  dense?: boolean;
  sections?: string[];
  onSelectTag?: TagSelectHandler;
  activeTags?: Record<string, string>;
  expandSignal?: DetailExpandSignal;
  onCollected?: () => void | Promise<void>;
}) {
  const summary = history.summary;
  const points = history.points || [];
  // ⚠️ 결측(`rank: null`)은 안 센다 — `Number(null) === 0` 이라 직접 재면 결측이 섞인다
  const chartable = countRankedPoints(points) >= MIN_CHART_POINTS;
  const signals = buildSignals({ product, summary });
  const price = formatPrice(product.displayPrice, product.currency);
  const show = (key: string) => !sections || sections.includes(key);
  const showAnyDetail = show('review') || show('spec') || show('media');
  const data = foundDetail(detail.data);
  const detailCollected = hasCollectedDetail(detail.data);
  const discountRate = product.discountRate ?? 0;
  const priceDropPct = summary?.priceDropPct ?? 0;

  if (product.ranked === false) {
    return (
      <UnrankedProductPanel
        product={product}
        detail={detail}
        dense={dense}
        sections={sections}
        onSelectTag={onSelectTag}
        activeTags={activeTags}
        expandSignal={expandSignal}
        onCollected={onCollected}
      />
    );
  }

  return (
    <>
      {show('metrics') ? (
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: dense ? 'repeat(2, 1fr)' : { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            mb: 2,
          }}
        >
          <MetricTile
            label="현재 순위"
            value={product.rank != null ? `${product.rank}위` : '-'}
            sub={
              product.prevRank != null
                ? `${product.prevDate ? `${ymdToShort(product.prevDate)} ` : '직전 '}${product.prevRank}위 대비`
                : '직전 비교 없음'
            }
          />
          <MetricTile
            label="최고 순위"
            value={summary?.bestRank != null ? `${summary.bestRank}위` : history.loading ? '…' : '-'}
            sub={summary?.days ? `수집 ${summary.days}일 기준` : ''}
          />
          <MetricTile
            label="현재 가격"
            value={price || '미제공'}
            sub={
              priceDropPct
                ? `${summary?.firstDate ? `${ymdToShort(summary.firstDate)} 대비 ` : ''}${
                    priceDropPct > 0 ? `${priceDropPct}% 인하` : `${Math.abs(priceDropPct)}% 인상`
                  }`
                : '변동 없음'
            }
            color={priceDropPct > 0 ? 'error.main' : undefined}
          />
          <MetricTile
            label="할인율"
            value={discountRate > 0 ? `${discountRate}%` : '없음'}
            sub={summary?.discountDelta ? `${summary.discountDelta > 0 ? '+' : ''}${summary.discountDelta}%p` : ''}
            color={discountRate > 0 ? 'error.main' : undefined}
          />
        </Box>
      ) : null}

      {show('signals') ? (
        <Box sx={{ mb: 2 }}>
          {history.loading ? (
            <Skeleton variant="rounded" width={220} height={26} />
          ) : (
            <SignalBadges signals={signals} />
          )}
        </Box>
      ) : null}

      {/* 상세페이지 AI 분석분이 있을 때만 */}
      {show('tags') && data?.hasTags ? (
        <Box sx={{ mb: 2 }}>
          <TagBlock tags={data.tags} onSelectTag={onSelectTag} activeTags={activeTags} />
        </Box>
      ) : null}

      {show('trend') ? (
        <>
          <Divider sx={{ my: 2 }} />
          <SectionHeading
            icon="solar:chart-2-linear"
            iconColor="primary.main"
            title={`${SOURCE_LABEL[product.source || ''] || product.source} 순위·가격 변동`}
          />

          {history.loading ? (
            <Skeleton variant="rounded" height={260} />
          ) : history.error ? (
            <Alert severity="error" variant="outlined">{history.error}</Alert>
          ) : chartable ? (
            <RankPriceChart points={points} currency={product.currency} />
          ) : points.length >= 1 ? (
            // ⚠️ 점이 하나면 그래프 대신 숫자로 — 한 점짜리 선은 읽을 것이 없다
            <Stack
              direction="row"
              spacing={3}
              sx={{ p: 2, borderRadius: RADIUS.md, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}
            >
              <Stack spacing={0.25}>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>순위</Typography>
                <Typography sx={{ fontWeight: 600, typography: 'uiHead' }}>
                  {points[0].rank != null ? `${points[0].rank}위` : '-'}
                </Typography>
              </Stack>
              <Stack spacing={0.25}>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>가격</Typography>
                <Typography sx={{ fontWeight: 600, typography: 'uiHead' }}>
                  {formatPrice(points[0].displayPrice, product.currency) || '-'}
                </Typography>
              </Stack>
              <Stack spacing={0.25} sx={{ justifyContent: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.disabled', maxWidth: 240 }}>
                  추이 그래프는 다음 갱신부터 그려져요.
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.disabled', py: 2 }}>이력 데이터가 없습니다.</Typography>
          )}
        </>
      ) : null}

      {show('cross') ? (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Iconify icon="solar:shop-2-linear" width={20} sx={{ color: 'info.main' }} />
            <Typography sx={{ fontWeight: 600 }}>타 판매채널 랭킹</Typography>
            {!cross.loading && cross.items.length ? (
              <Tag sx={{ bgcolor: 'grey.100', color: 'text.secondary' }}>{cross.items.length}건</Tag>
            ) : null}
          </Stack>

          {cross.loading ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2, color: 'text.secondary' }}>
              <CircularProgress size={18} />
              <Typography variant="caption">같은 브랜드·상품명으로 다른 채널을 대조하는 중…</Typography>
            </Stack>
          ) : cross.error ? (
            <Alert severity="error" variant="outlined">{cross.error}</Alert>
          ) : cross.items.length ? (
            <>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' } }}>
                {cross.items.map((item) => (
                  <CrossChannelCard key={`${item.source}:${item.postId}`} item={item} />
                ))}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.disabled' }}>
                채널마다 상품명 표기가 달라 브랜드·상품명 유사도로 대조합니다. 용량·기획 구성이 다를 수 있습니다.
              </Typography>
            </>
          ) : (
            // ⚠️ 「건너뛴 이유」가 있으면 그것을 먼저 — "못 찾았다"와 "안 찾았다"는 다른 말이다
            <Typography variant="body2" sx={{ color: 'text.disabled', py: 1.5 }}>
              {cross.note || '다른 판매채널의 랭킹에서는 이 상품을 찾지 못했습니다.'}
            </Typography>
          )}
        </>
      ) : null}

      {showAnyDetail && detail.loading ? (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1, color: 'text.secondary' }}>
            <CircularProgress size={16} />
            <Typography variant="caption">리뷰·상품 정보를 불러오는 중…</Typography>
          </Stack>
        </>
      ) : null}

      {data ? (
        <>
          {show('review') ? <ReviewBlock review={data.review} source={product.source} /> : null}
          {show('spec') ? <SpecBlock notice={data.notice} spec={data.spec} /> : null}
          {show('media') ? (
            <DetailMediaBlock
              media={data.media}
              textAll={data.detailTextAll}
              dense={dense}
              expandSignal={expandSignal}
              version={{
                mediaDate: data.mediaDate,
                changedAt: data.detailChangedAt,
                versionCount: data.detailVersionCount,
                // 직전 버전 대비 무엇이 달라졌나 — 버전 인덱스가 있을 때만 찬다
                current: data.detailVersion,
                // 개편 이력 — 칸을 누르면 그 시점 상세페이지를 되살린다
                timeline: data.detailVersions,
                shownHash: data.detailHash,
                ref: { postId: data.postId, source: data.source },
              }}
            />
          ) : null}
        </>
      ) : null}

      {/* 랭킹 상위권만 자동 수집한다 → 100위 밖 상품은 여기서 직접 건다 */}
      {showAnyDetail && !detail.loading && detail.data && !detailCollected ? (
        <>
          <Divider sx={{ my: 2 }} />
          <SectionHeading
            icon="solar:magic-stick-3-linear"
            iconColor="secondary.main"
            title="상세페이지"
          />
          <DetailCollectBar
            product={product}
            hasCollected={false}
            onCollected={onCollected}
            note={UNCOLLECTED_NOTE}
          />
        </>
      ) : null}
    </>
  );
}
